import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, MapPin, Briefcase, UsersThree, Buildings, Heart, ArrowRight,
  ChatCircleDots, CalendarStar, CheckCircle, Sparkle, Graph, Flower, Quotes,
} from "@phosphor-icons/react";

const P = {
  main: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/01299c49d1acfbf5cf160926a1af9a4d7b94b95a4be884a0b0cd85ec606b4dbf.jpeg",
  c1: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/de02b14dee886c3ed876bced80ee774a16bd9aba47886e1c8f0ea8e12965557b.jpeg",
  c2: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/766c3fc42116d4d634fbb6dd777addd21bfa58aec2794a5f88ff25fa5ba79763.jpeg",
  c3: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/517a19f681381a797f8c6e28a0246b6be007891615387180d1f75e36fc520189.jpeg",
  memorial: "https://static.prod-images.emergentagent.com/jobs/df41fdb8-3819-47be-a2f5-8e80426d9ef2/images/8f49b58efc02c6c45ab86c7c723db903d7fe990b9844e903b850105feb970995.jpeg",
};

const timeline = [
  { years: "2012 – 2016", inst: "Indian Institute of Technology, Bombay", detail: "B.Tech, Computer Science & Engineering · Batch of 2016", icon: GraduationCap },
  { years: "2008 – 2012", inst: "Delhi Public School, R.K. Puram", detail: "Senior Secondary · Science stream", icon: GraduationCap },
  { years: "2017 – 2019", inst: "Stanford University", detail: "M.S. Human-Computer Interaction · Class of 2019", icon: GraduationCap },
];

const classmates = [
  { img: P.c1, name: "Kenji Tanaka", via: "IIT Bombay · Batch 2016" },
  { img: P.c2, name: "Amara Okafor", via: "Stanford · Class 2019" },
  { img: P.c3, name: "Daniel Weber", via: "DPS R.K. Puram · 2012" },
];

const skills = ["Product Design", "Machine Learning", "Leadership", "Public Speaking", "Community Building", "Photography"];

const Section = ({ children, className = "" }) => (
  <motion.section
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6 }}
    className={className}
  >
    {children}
  </motion.section>
);

export default function SampleAlumni() {
  useSeo("A Sample Alumni Profile", "See what your AlumniPulse home looks like — profile, education timeline, network map and memorials.");

  return (
    <div className="bg-background" data-testid="sample-alumni-page">
      {/* Intro */}
      <section className="border-b border-border/60 bg-primary/[0.03]">
        <div className="mx-auto max-w-5xl px-5 py-14 text-center">
          <p className="overline text-secondary mb-4 inline-flex items-center gap-1.5"><Sparkle size={14} weight="fill" /> A guided preview</p>
          <h1 className="font-heading text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl">
            This is what your <span className="italic font-medium text-secondary">alumni home</span> looks like.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Meet <strong className="text-foreground">Ananya Sharma</strong> — a sample member. Everything below is a live preview of the profile, timeline, network map, and memorials you'll build once you join. No sign-up needed to look around.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register"><Button size="lg" className="rounded-full gap-2 px-7" data-testid="sample-register-cta">Build your own <ArrowRight size={18} /></Button></Link>
            <Link to="/alumni-groups"><Button size="lg" variant="outline" className="rounded-full px-7">Browse alumni groups</Button></Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-20 px-5 py-16">
        {/* Profile card */}
        <Section>
          <div className="overflow-hidden rounded-3xl border border-border/60 bg-card shadow-sm">
            <div className="h-32 bg-gradient-to-r from-primary/90 to-secondary/80 sm:h-40" />
            <div className="px-6 pb-8 sm:px-10">
              <div className="-mt-16 flex flex-col gap-5 sm:flex-row sm:items-end sm:gap-6">
                <img src={P.main} alt="Ananya Sharma" className="h-32 w-32 rounded-2xl border-4 border-card object-cover shadow-md sm:h-36 sm:w-36" data-testid="sample-avatar" />
                <div className="flex-1 pb-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-heading text-2xl font-medium">Ananya Sharma</h2>
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-medium text-secondary"><CheckCircle size={13} weight="fill" /> Verified alumna</span>
                  </div>
                  <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><Briefcase size={14} /> Senior Product Designer at Figma</p>
                  <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><MapPin size={14} /> San Francisco, USA · from New Delhi, India</p>
                </div>
                <div className="pb-1">
                  <Link to="/register"><Button className="rounded-full gap-1.5" data-testid="sample-connect-btn"><UsersThree size={16} /> Connect</Button></Link>
                </div>
              </div>

              <p className="mt-6 max-w-2xl leading-relaxed text-foreground/90">
                Designer, mentor and lifelong learner. I care about building things people love and staying close to the friends and teachers who shaped me. Always happy to reconnect with old classmates and swap stories from IIT and Stanford.
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {skills.map((s) => (
                  <span key={s} className="rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs text-foreground/80">{s}</span>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-3 gap-4 border-t border-border/60 pt-6 text-center sm:max-w-lg">
                {[["248", "Connections"], ["6", "Communities"], ["3", "Institutions"]].map(([n, l]) => (
                  <div key={l}>
                    <p className="font-heading text-2xl font-semibold text-primary">{n}</p>
                    <p className="text-xs text-muted-foreground">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Education timeline */}
        <Section>
          <div className="mb-8 max-w-2xl">
            <p className="overline text-secondary mb-2">Education timeline</p>
            <h2 className="font-heading text-3xl font-light">Your entire story, <span className="italic font-medium text-secondary">from first bell to final degree.</span></h2>
            <p className="mt-2 text-muted-foreground">Record every institution, campus, department, course, class and batch — kindergarten through grad school.</p>
          </div>
          <div className="relative ml-3 space-y-8 border-l-2 border-border/60 pl-8">
            {timeline.map((t) => (
              <div key={t.inst} className="relative">
                <span className="absolute -left-[42px] grid h-8 w-8 place-items-center rounded-full bg-accent text-primary ring-4 ring-background"><t.icon size={16} weight="duotone" /></span>
                <p className="text-xs font-medium text-secondary">{t.years}</p>
                <h3 className="mt-0.5 font-heading text-lg font-medium">{t.inst}</h3>
                <p className="text-sm text-muted-foreground">{t.detail}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Affiliation map snippet */}
        <Section>
          <div className="rounded-3xl border border-border/60 bg-gradient-to-br from-muted/40 to-accent/30 p-8 sm:p-12">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <p className="overline text-secondary mb-2 inline-flex items-center gap-1.5"><Graph size={14} weight="duotone" /> Affiliation map</p>
                <h2 className="font-heading text-3xl font-light">See your whole world, <span className="italic font-medium text-secondary">connected.</span></h2>
                <p className="mt-3 text-muted-foreground">An interactive graph places you at the centre, branching out to every institution, alumni group and classmate — grouped by country. Zoom, drag and explore the people around you.</p>
                <Link to="/register"><Button variant="secondary" className="mt-6 rounded-full gap-1.5" data-testid="sample-map-cta">Map my network <ArrowRight size={16} /></Button></Link>
              </div>
              {/* stylised mini graph */}
              <div className="relative mx-auto grid h-72 w-full max-w-sm place-items-center" data-testid="sample-map-graphic">
                <div className="absolute inset-0">
                  {[["top-4 left-8", P.c1, "IIT Bombay"], ["top-8 right-6", P.c2, "Stanford"], ["bottom-6 left-6", P.c3, "DPS Delhi"]].map(([pos, img, label]) => (
                    <div key={label} className={`absolute ${pos} flex flex-col items-center`}>
                      <img src={img} alt={label} className="h-14 w-14 rounded-full border-2 border-card object-cover shadow" />
                      <span className="mt-1 rounded-full bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground shadow-sm">{label}</span>
                    </div>
                  ))}
                </div>
                {/* memorial (deceased colleague) node — rustic badge */}
                <div className="absolute bottom-1 right-0 flex flex-col items-center" data-testid="sample-memorial-node">
                  <div className="relative rounded-2xl border border-amber-900/30 bg-gradient-to-b from-[#e8dcc4] to-[#cbb488] p-1.5 shadow-md">
                    <img src={P.memorial} alt="In memory of Prof. Menon" className="h-14 w-14 rounded-xl object-cover sepia" />
                    <span className="absolute -left-1.5 -top-1.5 grid h-6 w-6 place-items-center rounded-full bg-amber-800 text-white shadow ring-2 ring-[#e8dcc4]"><Flower size={12} weight="fill" /></span>
                  </div>
                  <span className="mt-1 rounded-full bg-[#4a3a22] px-2 py-0.5 text-[10px] font-medium text-[#f0e6d2] shadow-sm">Prof. Menon · D. 2019</span>
                </div>
                <div className="z-10 flex flex-col items-center">
                  <img src={P.main} alt="Ananya" className="h-20 w-20 rounded-full border-4 border-secondary object-cover shadow-lg" />
                  <span className="mt-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">You</span>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* People you may know */}
        <Section>
          <div className="mb-8 max-w-2xl">
            <p className="overline text-secondary mb-2 inline-flex items-center gap-1.5"><UsersThree size={14} weight="duotone" /> People you may know</p>
            <h2 className="font-heading text-3xl font-light">The classmates you've been <span className="italic font-medium text-secondary">meaning to find.</span></h2>
            <p className="mt-2 text-muted-foreground">Smart suggestions based on shared schools, batches and groups — so reconnecting is one tap away.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            {classmates.map((c) => (
              <div key={c.name} className="rounded-2xl border border-border/60 bg-card p-6 text-center shadow-sm">
                <img src={c.img} alt={c.name} className="mx-auto h-20 w-20 rounded-full object-cover" />
                <h3 className="mt-3 font-heading text-lg font-medium">{c.name}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">via {c.via}</p>
                <Link to="/register"><Button size="sm" variant="outline" className="mt-4 rounded-full gap-1.5"><UsersThree size={14} /> Connect</Button></Link>
              </div>
            ))}
          </div>
        </Section>

        {/* Communities + events strip */}
        <Section>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border border-border/60 bg-card p-7 shadow-sm">
              <span className="inline-grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary"><ChatCircleDots size={22} weight="duotone" /></span>
              <h3 className="mt-3 font-heading text-lg font-medium">A chat room per community</h3>
              <p className="mt-1 text-sm text-muted-foreground">Every class, batch and reunion group gets its own persistent chat — auto-created with the group.</p>
              <p className="mt-4 text-xs text-secondary">IIT Bombay · Batch of 2016 · 182 members · 12 online</p>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card p-7 shadow-sm">
              <span className="inline-grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary"><CalendarStar size={22} weight="duotone" /></span>
              <h3 className="mt-3 font-heading text-lg font-medium">Reunions & events</h3>
              <p className="mt-1 text-sm text-muted-foreground">Host reunions with RSVP, guest lists, photo memories and virtual links. Never miss a gathering.</p>
              <p className="mt-4 text-xs text-secondary">Class of 2016 · 10-Year Reunion · Dec 2026 · Goa</p>
            </div>
          </div>
        </Section>

        {/* In Memoriam */}
        <Section>
          <div className="mb-8 max-w-2xl">
            <p className="overline text-secondary mb-2 inline-flex items-center gap-1.5"><Flower size={14} weight="duotone" /> In Memoriam</p>
            <h2 className="font-heading text-3xl font-light">Remembering the people who <span className="italic font-medium text-secondary">shaped us.</span></h2>
            <p className="mt-2 text-muted-foreground">A dignified tribute wall for classmates, teachers and mentors we've lost — with photos, messages and shared memories.</p>
          </div>
          <div className="mx-auto max-w-md overflow-hidden rounded-3xl border border-border/60 bg-card shadow-sm" data-testid="sample-memorial-card">
            <div className="relative">
              <img src={P.memorial} alt="Prof. Rajan Menon" className="h-64 w-full object-cover grayscale" />
              <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-background/85 px-3 py-1 text-xs font-medium text-foreground backdrop-blur"><Flower size={13} weight="duotone" className="text-secondary" /> In loving memory</span>
            </div>
            <div className="p-6 text-center">
              <h3 className="font-heading text-xl font-medium">Prof. Rajan Menon</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">1948 – 2024 · Professor of Physics, IIT Bombay</p>
              <p className="mt-4 flex items-center justify-center gap-2 text-sm italic leading-relaxed text-foreground/80">
                <Quotes size={16} className="text-secondary" /> A teacher who made us believe we could do anything.
              </p>
              <div className="mt-5 flex items-center justify-center gap-5 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Heart size={15} weight="fill" className="text-secondary" /> 214 tributes</span>
                <span className="inline-flex items-center gap-1.5"><ChatCircleDots size={15} /> 68 memories</span>
              </div>
              <Link to="/memorials"><Button variant="outline" className="mt-6 rounded-full gap-1.5" data-testid="sample-memorial-cta">Visit the memorial wall <ArrowRight size={15} /></Button></Link>
            </div>
          </div>
        </Section>

        {/* Final CTA */}
        <Section className="rounded-3xl bg-primary px-8 py-14 text-center text-primary-foreground sm:px-12">
          <Buildings size={36} weight="duotone" className="mx-auto opacity-90" />
          <h2 className="mt-4 font-heading text-3xl font-light sm:text-4xl">Your story deserves a home.</h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/80">Build your profile, find your people, and keep every memory in one place — free to start.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register"><Button size="lg" variant="secondary" className="rounded-full gap-2 px-8" data-testid="sample-final-cta">Get started free <ArrowRight size={18} /></Button></Link>
            <Link to="/alumni-groups"><Button size="lg" variant="ghost" className="rounded-full px-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">Explore groups</Button></Link>
          </div>
        </Section>
      </div>
    </div>
  );
}
